import { Skeleton, Stack } from '@mui/material'
import { FeatureFlagIdReviewsPendingActions } from 'c1gen/features'
import { TaskV1TaskActionsServiceApprove, TaskV1TaskActionsServiceApproveWithStepUp, TaskV1TaskActionsServiceDeny, TaskV1TaskActionsServiceMarkProvisionComplete } from 'c1gen/permissions'
import { AccountDeletedPrimaryAction } from 'components/core/task-list-v2/task-list-v2-components/AccountDeletedPrimaryAction'
import { ApproveOrDenyToggle } from 'components/core/task-list-v2/task-list-v2-components/ApproveOrDenyToggle'
import { SelfApprovalNotAllowedPrimaryAction } from 'components/core/task-list-v2/task-list-v2-components/SelfApprovalNotAllowedPrimaryAction'
import { useFeatureChecker, useHasAnyPermission, useHasBuiltinRole } from 'components/no-ui/capability'
import { isSelfActionAllowed } from 'components/pages/task/details'
import { TaskStatusLabel } from 'components/pages/task/details/content/TaskStatusLabel'
import { canActOn } from 'components/pages/task/state'
import { TaskValue } from 'lib/actions/task'
import { RoleID } from 'lib/constants/system'
import { UserContext } from 'lib/context/app'
import { PendingActionState } from 'pbts/c1/api/accessreview/v1/accessreview_pending'
import { TaskActionType } from 'pbts/c1/api/task/v1/task'
import { FC, useContext, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { PendingActionsStateV2, selectConsolidatedPendingActionState, selectIsLoadingPendingActionStates } from 'redux_toolkit/slices/pendingActionsSliceV2'

export type TaskPrimaryActionsV2Variant = 'default' | 'icon-button' | 'contained'

type TaskPrimaryActionsPendingV2Props = {
    task: TaskValue
    variant?: TaskPrimaryActionsV2Variant
    neverShowTaskStatusLabel?: boolean
}

export const TaskPrimaryActionsPendingV2: FC<TaskPrimaryActionsPendingV2Props> = (
    { task, neverShowTaskStatusLabel = false, variant = 'default' },
) => {
    const hasPendingActionsFeature = useFeatureChecker(FeatureFlagIdReviewsPendingActions) ?? false
    const userContext = useContext(UserContext)
    const userId = userContext.user.id
    const isLoadingPendingActionStates = useSelector(selectIsLoadingPendingActionStates)

    const consolidatedTaskToPendingActionState = useSelector<{ pendingActionsV2: PendingActionsStateV2 }, PendingActionState | undefined>(
        (state) => selectConsolidatedPendingActionState(state, task.id),
    )

    const selfApprovalNotAllowed = useMemo(
        () => task.userId === userId && !isSelfActionAllowed(task) && !!task?.stepApproverIds?.includes(userId),
        [task, userId],
    )

    const taskCannotTakeAction = useMemo(
        () =>
            !canActOn(task, TaskActionType.APPROVE) &&
            !canActOn(task, TaskActionType.APPROVE_WITH_STEP_UP) &&
            !canActOn(task, TaskActionType.DENY) &&
            !canActOn(task, TaskActionType.PROVISION_COMPLETE),
        [task],
    )

    const isReadOnlySuperAdmin = useHasBuiltinRole(RoleID.ReadOnlySuperAdmin)
    const userCanTakeAction = useHasAnyPermission([
        TaskV1TaskActionsServiceApproveWithStepUp,
        TaskV1TaskActionsServiceApprove,
        TaskV1TaskActionsServiceDeny,
        TaskV1TaskActionsServiceMarkProvisionComplete,
    ])

    if (!hasPendingActionsFeature && task.type?.taskType.oneofKind === 'certify') {
        return null
    }

    if (task.appUser?.deletedAt) {
        return <AccountDeletedPrimaryAction task={task} variant={variant} />
    }

    if (selfApprovalNotAllowed) {
        return <SelfApprovalNotAllowedPrimaryAction task={task} variant={variant} />
    }

    if ((isReadOnlySuperAdmin && !userCanTakeAction) || (!neverShowTaskStatusLabel && taskCannotTakeAction)) {
        return <TaskStatusLabel task={task} />
    }

    // If we are loading, show a skeleton.
    if (isLoadingPendingActionStates) {
        return <Stack direction="row" spacing={1} alignItems="center">
            <Skeleton variant="rectangular" width={variant === 'icon-button' ? 32 : 80} height={variant === 'icon-button' ? 32 : 36} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" width={variant === 'icon-button' ? 32 : 80} height={variant === 'icon-button' ? 32 : 36} sx={{ borderRadius: 1 }} />
        </Stack>
    }

    return <Stack direction="row" spacing={1} alignItems="center">
        <ApproveOrDenyToggle task={task} variant={variant} pendingActionState={consolidatedTaskToPendingActionState} />
    </Stack>
}
